import { CustomTooltip } from '@/shared/ui/components';
import { generateBeatTypeString, generateHesBeatStatus } from '../../helper';
import { useBeatMarkerButton } from './useBeatMarkerButtons';
import type { BeatMarkerButtonsProps } from './beatMarkerButtons.types';
import BeatDeleteActiveIcon from './assets/beat-delete-active-icon.svg';
import BeatNActiveIcon from './assets/beat-n-active-icon.svg';
import BeatQActiveIcon from './assets/beat-q-active-icon.svg';
import BeatSActiveIcon from './assets/beat-s-active-icon.svg';
import BeatVActiveIcon from './assets/beat-v-active-icon.svg';
import BeatDeleteIcon from './assets/black-delete-icon.svg';
import CloseActiveIcon from './assets/close-active-icon.svg';
import CloseIcon from './assets/close-icon.svg';
import BeatNIcon from './assets/swap-beat-n-icon.svg';
import BeatQIcon from './assets/swap-beat-q-icon.svg';
import BeatSIcon from './assets/swap-beat-s-icon.svg';
import BeatVIcon from './assets/swap-beat-v-icon.svg';

/**
 * Icon button for a single beat-marker tool that swaps its image while hovered or active. Tracks hover state through useBeatMarkerButton, ignores mouse over and out while disabled, and stays clickable when active even if disable is set.
 *
 * @param id - DOM id assigned to the button.
 * @param src - Icon shown in the idle state.
 * @param srcOnHover - Icon shown while hovered or active.
 * @param alt - Alt text for the icon image.
 * @param onClick - Handler fired when the button is clicked.
 * @param isActive - Whether the tool is currently selected.
 * @param disable - Whether the button is disabled while inactive.
 * @returns The rendered button element.
 */
const BeatMarkerButton = ({ id, src, srcOnHover, alt, onClick, isActive, disable }: any) => {
  const { isHover, setIsHover } = useBeatMarkerButton();
  return (
    <button
      type="button"
      id={id}
      disabled={disable && !isActive}
      onMouseOver={() => !disable && setIsHover(true)}
      onMouseOut={() => !disable && setIsHover(false)}
      onClick={onClick}
      onFocus={() => {}}
      onBlur={() => {}}
    >
      <img src={isActive || isHover ? srcOnHover : src} alt={alt} />
    </button>
  );
};

const BeatMarkerButtons = ({
  beatTypeNumber = null,
  onCloseARBeatMode,
  handleChangeBeat,
  isSelectingBeat,
}: BeatMarkerButtonsProps) => {
  const beatTypeString = generateBeatTypeString(beatTypeNumber);
  const listBeatBtn = [
    {
      src: BeatNIcon,
      srcOnHover: BeatNActiveIcon,
      alt: 'Beat icon',
      id: 'beatMarkerN',
      active: beatTypeString === 'N',
      onClick: () => handleChangeBeat(generateHesBeatStatus('N')),
      disable: isSelectingBeat,
      tooltipText: (
        <div className="beat-tooltip">
          Add/Change N<span className="shortkey">N</span>
        </div>
      ),
    },
    {
      src: BeatSIcon,
      srcOnHover: BeatSActiveIcon,
      alt: 'Beat icon',
      id: 'beatMarkerS',
      active: beatTypeString === 'S',
      disable: isSelectingBeat,
      onClick: () => handleChangeBeat(generateHesBeatStatus('S')),
      tooltipText: (
        <div className="beat-tooltip">
          Add/Change S<span className="shortkey">S</span>
        </div>
      ),
    },
    {
      src: BeatVIcon,
      srcOnHover: BeatVActiveIcon,
      alt: 'Beat icon',
      id: 'beatMarkerV',
      active: beatTypeString === 'V',
      disable: isSelectingBeat,
      onClick: () => handleChangeBeat(generateHesBeatStatus('V')),
      tooltipText: (
        <div className="beat-tooltip">
          Add/Change V<span className="shortkey">V</span>
        </div>
      ),
    },
    {
      src: BeatQIcon,
      srcOnHover: BeatQActiveIcon,
      alt: 'Beat icon',
      id: 'beatMarkerQ',
      active: beatTypeString === 'Q',
      disable: isSelectingBeat,
      onClick: () => handleChangeBeat(generateHesBeatStatus('Q')),
      tooltipText: (
        <div className="beat-tooltip">
          Add/Change Q<span className="shortkey">Q</span>
        </div>
      ),
    },
    {
      type: 'divider',
    },
    {
      src: BeatDeleteIcon,
      srcOnHover: BeatDeleteActiveIcon,
      alt: 'Beat icon',
      id: 'beatMarkerDelete',
      active: beatTypeString === 'DEL',
      disable: isSelectingBeat,
      onClick: () => handleChangeBeat(generateHesBeatStatus('DEL')),
      tooltipText: (
        <div className="beat-tooltip">
          Delete beat
          <span className="shortkey">D</span>
        </div>
      ),
    },
  ];

  const listFunctionBtn = [
    // {
    //   src: QuestionIcon,
    //   srcOnHover: QuestionActiveIcon,
    //   alt: 'Question icon',
    //   id: 'beatMarkQuestion',
    //   tooltipText: (
    //     <div className="beat-tooltip">
    //       Guide
    //     </div>
    //   ),
    // },
    // {
    //   type: 'divider',
    // },
    {
      src: CloseIcon,
      srcOnHover: CloseActiveIcon,
      alt: 'Close icon',
      id: 'beatMarkerClose',
      tooltipText: (
        <div className="beat-tooltip">
          Exit
          <span className="shortkey">ESC</span>
        </div>
      ),
      onClick: onCloseARBeatMode,
    },
  ];

  /**
   * Renders the beat-marker toolbar row from a list of tool descriptors. Emits a vertical divider for entries whose type is divider and otherwise a tooltip-wrapped BeatMarkerButton carrying the entry's icons, click handler, active and disable flags.
   *
   * @param list - Tool descriptors to render, defaulting to an empty list.
   * @returns The rendered toolbar entries.
   */
  const renderButton = (list: any = []) =>
    list.map((item: any) =>
      item.type === 'divider' ? (
        <div className="vertical-divide" />
      ) : (
        <CustomTooltip title={item.tooltipText} placement="bottom">
          <div>
            <BeatMarkerButton
              id={item.id}
              src={item.src}
              srcOnHover={item.srcOnHover}
              alt={item.alt}
              onClick={item.onClick}
              isActive={item.active}
              disable={item.disable}
            />
          </div>
        </CustomTooltip>
      ),
    );

  return (
    <div id="beatMarkerButtons">
      <div className="beat-button">{renderButton(listBeatBtn)}</div>
      <div className="function-button">{renderButton(listFunctionBtn)}</div>
    </div>
  );
};

export default BeatMarkerButtons;
